import { useCallback, useReducer, useRef } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  createSale,
  updateSale,
  finalizeSale,
  getSaleItems,
  createBill,
  priceBill,
  applyBillDiscount,
  approveBillDiscount,
  recheckBill,
  finalizeBill,
  submitBillPayment,
  buildSalePayload,
  buildSaleDiff,
  buildBillPayload,
} from '../../../api/pharmacyDispense';
import { resolvePharmacyScope, getPharmacyRole, getPharmacyActorId } from '../../../hooks/usePharmacyRole';
import { parseMoney } from '../../../utils';
import { toInventoryList } from '../../../api/inventory-request';

const INITIAL = {
  saleId: null,
  saleFinalized: false,
  transactionItemIds: [],
  saleItems: [],
  // Last transaction_payload sent for this sale, so an unchanged identity
  // does not trigger a PATCH on every keystroke.
  salePatientPayload: null,
  billId: null,
  // Human-readable BILL-... number; the UUID above is only for API calls.
  billNumber: null,
  billLineIds: [],
  amountPayable: null,
  billStatus: null,
  discountState: null,
  paymentResult: null,
  lastFailedStep: null,
  lastError: null,
  pipelineBusy: false,
  pipelineLabel: '',
  scope: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return { ...INITIAL };
    case 'PATCH':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

function extractId(row, ...keys) {
  if (!row || typeof row !== 'object') return null;
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return row.id || null;
}

function extractSaleItems(payload) {
  const list = toInventoryList(payload);
  return list.map((row) => ({
    transaction_item_id: extractId(
      row,
      'inventory_transaction_item_id',
      'transaction_item_id',
      'id',
      'sale_item_id',
    ),
    item_id: extractId(row, 'item_id', 'product_id'),
    batch_number: row.batch_number,
    ...row,
  })).filter((r) => r.transaction_item_id);
}

export function matchSaleItemForMedicineLine(line, saleItems) {
  const itemId = String(line?.medicine_id || line?.medicine?.item_id || '');
  if (!itemId) return null;
  const batchNumbers = new Set(
    (line?.batchLines || []).map((bl) => String(bl.batch_number || '')).filter(Boolean),
  );
  const candidates = (saleItems || []).filter(
    (si) => String(si.item_id || si.product_id || '') === itemId,
  );
  if (!candidates.length) return null;
  if (batchNumbers.size) {
    const batchMatch = candidates.find((si) => batchNumbers.has(String(si.batch_number || '')));
    if (batchMatch) return batchMatch;
  }
  return candidates[0];
}

function isBillableSaleItem(item) {
  const status = String(item?.billing_status || 'PENDING').toUpperCase();
  return status === 'PENDING';
}

/** Load sale items and return only lines the bill API accepts (billing_status=PENDING). */
async function loadBillableSaleItems(saleId, scope) {
  const itemsPayload = await getSaleItems(saleId, scope);
  const saleItems = extractSaleItems(itemsPayload);
  const billable = saleItems.filter(isBillableSaleItem);
  return { saleItems, billable };
}

function samePatientPayload(a, b) {
  const norm = (p) => JSON.stringify({
    patient_name: p?.patient_name || '',
    patient_mobile: p?.patient_mobile || '',
  });
  return norm(a) === norm(b);
}

/**
 * Create the draft sale, or bring the existing one in line with the basket via
 * PATCH. It must stay the SAME sale id for its whole life: the service queues
 * a controlled-drug authorization against each line the moment the draft is
 * written, and replacing the sale would strand every one of them.
 */
async function syncDraftSale({ lines, storeId, store, remarks, patientDisplay, prev, patch }) {
  const scope = resolvePharmacyScope(store);
  if (!scope.tenant_id || !scope.hospital_id) {
    throw new Error('Store is missing tenant/hospital scope — cannot create sale');
  }
  if (!storeId) {
    throw new Error('No pharmacy store available');
  }

  const saleBody = buildSalePayload({
    lines,
    storeId,
    scope,
    remarks,
    transactionDate: dayjs().format('YYYY-MM-DD'),
    patientDisplay,
  });
  if (!saleBody.items.length) {
    throw new Error('Add at least one medicine with a valid quantity');
  }
  const patientPayload = saleBody.transaction_payload || null;

  let saleId = prev.saleId;
  let saleItems = prev.saleItems || [];

  if (saleId && prev.saleFinalized) {
    // Stock is already out; the basket is frozen.
    return { saleId, saleItems, scope };
  }

  if (saleId) {
    const diff = buildSaleDiff(saleItems, saleBody.items);
    const patientChanged = !samePatientPayload(prev.salePatientPayload, patientPayload);
    if (diff || patientChanged) {
      const body = { ...(diff || {}) };
      if (patientChanged && patientPayload) body.transaction_payload = patientPayload;
      await updateSale(saleId, body, scope);
      saleItems = extractSaleItems(await getSaleItems(saleId, scope));
    }
  } else {
    const sale = await createSale(saleBody);
    saleId = extractId(sale, 'inventory_transaction_id', 'sale_id', 'id', 'transaction_id');
    if (!saleId) throw new Error('Sale created but no sale_id returned');
    saleItems = extractSaleItems(await getSaleItems(saleId, scope));
    if (!saleItems.length) throw new Error('Sale created but no transaction items returned');
  }

  patch({
    saleId,
    saleFinalized: false,
    saleItems,
    salePatientPayload: patientPayload,
    transactionItemIds: saleItems.map((i) => i.transaction_item_id),
    scope,
  });

  return { saleId, saleItems, scope };
}

/**
 * Bring the draft in line with the basket, then finalize it once.
 * The sale is never recreated: finalize hard-gates controlled lines on their
 * AUTHORIZED record, so a fresh sale would fail the gate rather than dodge it.
 */
async function ensureBillableSale({
  lines,
  storeId,
  store,
  remarks,
  patientDisplay,
  existingSaleId,
  scope: resumeScope,
  prev,
  patch,
}) {
  let saleId;
  let scope;
  let saleItems;

  if (existingSaleId) {
    // Resuming a parked order: the basket is not in memory here, so the sale
    // must be taken exactly as it stands. Running the diff against an empty
    // `lines` would delete every line on it.
    saleId = existingSaleId;
    scope = resumeScope || resolvePharmacyScope(store);
    // Only worth fetching when finalize has already happened. Otherwise the
    // post-finalize reload below replaces this result outright, so paying for
    // it here is a wasted round-trip.
    saleItems = prev.saleFinalized
      ? (await loadBillableSaleItems(saleId, scope)).saleItems
      : [];
    patch({ saleId, saleItems, scope });
  } else {
    const draft = await syncDraftSale({
      lines,
      storeId,
      store,
      remarks,
      patientDisplay,
      prev,
      patch,
    });
    saleId = draft.saleId;
    scope = draft.scope;
    saleItems = draft.saleItems;
  }

  let billable = saleItems.filter(isBillableSaleItem);

  if (!prev.saleFinalized) {
    await finalizeSale(saleId, {}, scope);
    patch({ saleFinalized: true });
    ({ saleItems, billable } = await loadBillableSaleItems(saleId, scope));
  }

  if (!billable.length) {
    throw new Error('No billable sale lines found for this dispense.');
  }

  const transactionItemIds = billable.map((i) => i.transaction_item_id);
  patch({ saleId, saleItems, transactionItemIds, scope });
  return { saleId, saleItems: billable, scope, transactionItemIds };
}

/**
 * Orchestrates the canonical sale → bill → payment pipeline.
 * Supports resume-from-failure after sale finalize (never re-POSTs sale/finalize).
 */
export default function useDirectDispensePipeline() {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const stateRef = useRef(state);

  const patch = useCallback((payload) => {
    stateRef.current = { ...stateRef.current, ...payload };
    dispatch({ type: 'PATCH', payload });
  }, []);

  const resetPipeline = useCallback(() => {
    stateRef.current = { ...INITIAL };
    dispatch({ type: 'RESET' });
  }, []);

  const ensureDraftSale = useCallback(async ({
    lines,
    storeId,
    store,
    remarks,
    patientDisplay,
  }) => syncDraftSale({
    lines,
    storeId,
    store,
    remarks,
    patientDisplay,
    prev: stateRef.current,
    patch,
  }), [patch]);

  const runPrepareBill = useCallback(async ({
    lines,
    storeId,
    store,
    patientDisplay,
    remarks,
    /** Set when collecting on an order that already exists (parked/resumed). */
    existingSaleId,
    /** Scope of a resumed order — it comes off the sale, not a store picker. */
    scope: resumedScope,
  }) => {
    let scope = resolvePharmacyScope(store, resumedScope || {});
    // Only creating a sale needs a store to hang tenant/hospital off. A resumed
    // order already carries its own scope on the sale record, so requiring a
    // store here blocked the collect step on the order-detail screen — which
    // deliberately has no store picker.
    if (!existingSaleId && (!scope.tenant_id || !scope.hospital_id)) {
      throw new Error('Store is missing tenant/hospital scope — cannot create sale');
    }

    let billId = stateRef.current.billId;
    let amountPayable = stateRef.current.amountPayable;
    let billStatus = stateRef.current.billStatus;
    let billLineIds = stateRef.current.billLineIds || [];

    patch({ pipelineBusy: true, lastError: null, scope });

    try {
      patch({ pipelineLabel: 'Preparing sale…', lastFailedStep: 'sale_create' });
      const billableSale = await ensureBillableSale({
        lines,
        storeId,
        store,
        remarks,
        patientDisplay,
        existingSaleId,
        scope,
        prev: stateRef.current,
        patch,
      });
      const { saleId, transactionItemIds, scope: stageScope } = billableSale;
      scope = stageScope;

      if (!billId) {
        patch({ pipelineLabel: 'Preparing bill…', lastFailedStep: 'bill_create' });
        const billBody = buildBillPayload({
          scope,
          store,
          storeId,
          transactionItemIds,
          patientDisplay,
        });
        const bill = await createBill(billBody);
        billId = extractId(bill, 'bill_id', 'id');
        if (!billId) throw new Error('Bill created but no bill_id returned');
        const linesList = toInventoryList(bill.lines || bill.items || bill.bill_lines || []);
        billLineIds = linesList.map((l) => extractId(l, 'bill_line_id', 'id')).filter(Boolean);
        billStatus = bill.bill_status || bill.status || 'DRAFT';
        patch({ billId, billNumber: bill.bill_number || null, billLineIds, billStatus });
      }

      patch({ pipelineLabel: 'Pricing…', lastFailedStep: 'bill_price' });
      const priced = await priceBill(billId, scope);
      amountPayable = priced?.amount_payable ?? priced?.data?.amount_payable ?? amountPayable;
      billStatus = priced?.bill_status || priced?.status || billStatus || 'DRAFT';
      const pricedLines = toInventoryList(priced?.lines || priced?.items || []);
      if (pricedLines.length) {
        billLineIds = pricedLines.map((l) => extractId(l, 'bill_line_id', 'id')).filter(Boolean);
      }

      patch({
        amountPayable,
        billStatus,
        billLineIds,
        lastFailedStep: null,
        pipelineBusy: false,
        pipelineLabel: '',
        scope,
      });

      return {
        saleId, billId, transactionItemIds, amountPayable, billStatus, billLineIds, scope,
      };
    } catch (err) {
      patch({
        pipelineBusy: false,
        pipelineLabel: '',
        lastError: err?.message || 'Pipeline failed',
      });
      throw err;
    }
  }, [patch]);

  const applyDiscount = useCallback(async (amount, scope) => {
    const cur = stateRef.current;
    if (!cur.billId) throw new Error('No bill to discount');
    const body = {
      bill_discount_amount: String(parseMoney(amount).toFixed(4)),
      approver_role: getPharmacyRole() || 'CASHIER',
    };
    const result = await applyBillDiscount(cur.billId, body, scope || cur.scope);
    const billStatus = result?.bill_status || result?.status;
    const amountPayable = result?.amount_payable;
    patch({
      billStatus,
      amountPayable: amountPayable ?? cur.amountPayable,
      discountState: {
        status: billStatus,
        amount: parseMoney(amount),
        raw: result,
      },
    });
    return result;
  }, [patch]);

  const approveDiscount = useCallback(async (approvedBy, scope) => {
    const cur = stateRef.current;
    if (!cur.billId) throw new Error('No bill');
    const result = await approveBillDiscount(
      cur.billId,
      { approved_by: approvedBy },
      scope || cur.scope,
    );
    patch({
      billStatus: result?.bill_status || result?.status || 'DRAFT',
      amountPayable: result?.amount_payable ?? cur.amountPayable,
      discountState: {
        ...(cur.discountState || {}),
        status: 'APPROVED',
        approved_by: approvedBy,
        approved_at: result?.discount_approved_at,
        raw: result,
      },
    });
    return result;
  }, [patch]);

  const collectPayment = useCallback(async (modes, scope) => {
    const cur = stateRef.current;
    if (!cur.billId) throw new Error('No bill');
    if (cur.billStatus === 'PENDING_APPROVAL') {
      throw new Error('Discount is pending supervisor approval');
    }

    const sc = scope || cur.scope;
    await recheckBill(cur.billId, { rechecked_by: getPharmacyActorId() }, sc);
    await finalizeBill(cur.billId, { finalized_by: getPharmacyActorId() }, sc);

    const payment = await submitBillPayment(cur.billId, { modes }, sc);
    patch({
      paymentResult: payment,
      billStatus: 'PAID',
    });
    return payment;
  }, [patch]);

  /**
   * Recheck + finalize the replacement bill WITHOUT taking payment: an exchange
   * settles it against the refund server-side instead. Safe to call again after
   * a failed exchange -- a bill that is already FINALIZED is left as it is.
   */
  const finalizeForExchange = useCallback(async (scope) => {
    const cur = stateRef.current;
    if (!cur.billId) throw new Error('No bill');
    if (cur.billStatus === 'PENDING_APPROVAL') {
      throw new Error('Discount is pending supervisor approval');
    }
    if (cur.billStatus === 'FINALIZED') return cur.billId;

    const sc = scope || cur.scope;
    await recheckBill(cur.billId, { rechecked_by: getPharmacyActorId() }, sc);
    await finalizeBill(cur.billId, { finalized_by: getPharmacyActorId() }, sc);
    patch({ billStatus: 'FINALIZED' });
    return cur.billId;
  }, [patch]);

  return {
    state,
    resetPipeline,
    ensureDraftSale,
    runPrepareBill,
    applyDiscount,
    approveDiscount,
    collectPayment,
    finalizeForExchange,
    toastError: (err) => toast.error(err?.message || 'Something went wrong'),
  };
}
